import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { CYRILLIC_REGEXP } from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
//eslint-disable-next-line max-len
import ValidateRecipientAdditionalDetailsModel from 'components/NewPaymentFormV2/models/ValidateRecipientAdditionalDetailsModel';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './RecipientAdditionalDetailsStep.scss';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';

const RecipientAdditionalDetailsStep = ({ newPaymentStore, customerNumber, userAccounts }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      registrationNumber: newPaymentStore.recipientAdditionalDetailsStep?.registrationNumber || '',
      country: newPaymentStore.recipientAdditionalDetailsStep?.country || '',
      city: newPaymentStore.recipientAdditionalDetailsStep?.city || '',
      postCode: newPaymentStore.recipientAdditionalDetailsStep?.postCode || '',
      address: newPaymentStore.recipientAdditionalDetailsStep?.address || ''
    },
    validationSchema: Yup.object().shape({
      registrationNumber: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      country: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      city: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(50, i18n.getMessage('client.validation.message.maxSize', { size: 50 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      postCode: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(15, i18n.getMessage('client.validation.message.maxSize', { size: 15 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      address: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        )
    }),
    onSubmit: async (values) => {
      const requestData = ValidateRecipientAdditionalDetailsModel({
        ...newPaymentStore.recipientAdditionalDetailsStep,
        ...values
      });

      newPaymentStore.updateRecipientAdditionalDetailsStep(values);

      await newPaymentStore.validateAdditionalRecipientDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  const allCodes = userAccounts?.flatMap((acct) => acct.countries) ?? [];
  const uniqueCodes = [...new Set(allCodes)];
  const countriesOptions = uniqueCodes.map((code) => ({
    key: code,
    value: i18n.getMessage(`country.${code}`)
  }));

  const handleCountryChange = (name, data) => handleChange({ target: { name, value: data } });

  return (
    <form onSubmit={form.handleSubmit} className={'new-payment-recipient-additional-details-step'}>
      <div className={'new-payment-recipient-additional-details-step-inputs-wrapper'}>
        <Input
          className={'new-payment-recipient-additional-details-step-input'}
          name={'registrationNumber'}
          label={i18n.getMessage('templates.form.registrationNumber.label')}
          placeholder={i18n.getMessage('templates.form.registrationNumber.placeholder')}
          value={values.registrationNumber}
          error={
            errors.registrationNumber ||
            getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'registrationNumber')
          }
          onChange={handleChange}
          isRequired
        />
        <InputDropDownSearch
          autoComplete={'off'}
          className={'new-payment-recipient-additional-details-step-input'}
          name={'country'}
          label={i18n.getMessage('templates.form.country.label')}
          placeholder={i18n.getMessage('templates.form.country.placeholder')}
          options={countriesOptions}
          value={values.country}
          error={errors.country || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'country')}
          onChange={handleCountryChange}
          isRequired
        />
        <Input
          className={'new-payment-recipient-additional-details-step-input'}
          name={'city'}
          label={i18n.getMessage('templates.form.city.label')}
          placeholder={i18n.getMessage('templates.form.city.placeholder')}
          value={values.city}
          error={errors.city || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'city')}
          onChange={handleChange}
          isRequired
        />
        <Input
          className={'new-payment-recipient-additional-details-step-input'}
          name={'postCode'}
          label={i18n.getMessage('templates.form.postCode.label')}
          placeholder={i18n.getMessage('templates.form.postCode.placeholder')}
          value={values.postCode}
          error={errors.postCode || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'postCode')}
          onChange={handleChange}
          isRequired
        />
        <Input
          className={'new-payment-recipient-additional-details-step-input new-payment-address-input'}
          name={'address'}
          label={i18n.getMessage('templates.form.address.label')}
          placeholder={i18n.getMessage('templates.form.address.placeholder')}
          value={values.address}
          error={errors.address || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'address')}
          onChange={handleChange}
          isRequired
        />
      </div>
      <div className={'new-payment-step-buttons'}>
        <Button
          className={'new-payment-recipient-additional-details-step-next-button'}
          type={'primary'}
          roleType={'submit'}
          onClick={handleSubmit}
          isDisabled={newPaymentStore.isLoading}
        >
          {i18n.getMessage('templates.form.button.next')}
        </Button>
      </div>
    </form>
  );
};

RecipientAdditionalDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore
}))(observer(RecipientAdditionalDetailsStep));
